import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Calculator, ShieldCheck, ChevronRight, Upload, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from '../api/client';
import { API } from '../api/config';
import { stripeElementStyle } from '../stripeElementStyle';
import { calculateEstimatedReward } from '../utils/reward';
import { calculateStage1Quote, Stage1Quote } from '../utils/stage1';

const stripePk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx';
const stripePromise = loadStripe(stripePk);

interface OrderItem {
  id: string;
  retailer: string;
  url: string;
  price: number;
  category: string;
  quantity: number;
  size: string;
  color: string;
  discountCode: string;
}

const RETAILERS = [
  'Selfridges',
  'Harrods',
  'Liberty London',
  'Farfetch',
  'Net-a-Porter',
  'MatchesFashion',
  'John Lewis',
  'Boots',
  'Marks & Spencer',
  'Amazon UK',
  'Other (Compliant Retailer)'
];

const CATEGORIES = [
  { label: 'Jewelry / Small Leather (珠寶/小皮件)', value: 'jewelry' },
  { label: 'Bags / Shoes (包款/鞋履)', value: 'bags' },
  { label: 'Ready-to-wear / Bulky (成衣/大件行李)', value: 'bulky' },
  { label: 'Beauty / Skincare (美妝/保養品)', value: 'beauty' },
  { label: 'Homeware / Kitchen (家居/廚具)', value: 'homeware' },
  { label: 'Electronics (電子產品)', value: 'electronics' },
  { label: 'Other General Goods (其他一般商品)', value: 'general' },
];

type OrderPaymentMethod = 'card' | 'bank_transfer' | 'reward';

const OrderPortal: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<OrderItem[]>([{
    id: Math.random().toString(36).substr(2, 9),
    retailer: '',
    url: '',
    price: 0,
    category: 'general',
    quantity: 1,
    size: '',
    color: '',
    discountCode: '',
  }]);
  const [step, setStep] = useState(1);
  const [compliance, setCompliance] = useState({
    flightSafety: false,
    quotaAcknowledge: false
  });
  const [quote, setQuote] = useState<number | null>(null);
  const [stage1Breakdown, setStage1Breakdown] = useState<Stage1Quote | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<OrderPaymentMethod>('card');
  const [completedPaymentMethod, setCompletedPaymentMethod] = useState<OrderPaymentMethod>('card');
  const [rewardBalance, setRewardBalance] = useState<number | null>(null);
  const [listImage, setListImage] = useState<File | null>(null);
  const [listImageError, setListImageError] = useState<string | null>(null);
  const listFileInputRef = useRef<HTMLInputElement>(null);

  const handleListImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setListImageError('Please upload a JPEG, PNG, or WEBP image.');
      return;
    }
    setListImageError(null);
    setListImage(file);
  };

  React.useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    axios.get(API.getRewardBalance(), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setRewardBalance(res.data.balance))
      .catch(() => {});
  }, []);

  const addItem = () => {
    if (items.length < 5) {
      setItems([...items, {
        id: Math.random().toString(36).substr(2, 9),
        retailer: '',
        url: '',
        price: 0,
        category: 'general',
        quantity: 1,
        size: '',
        color: '',
        discountCode: '',
      }]);
    }
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof OrderItem, value: any) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  // Stage-1 two-stage billing (兩段式計費) procurement quote — mirrors the
  // backend engine (OrderService._compute_stage1). Computed live from the
  // current items + paymentMethod so that the displayed quote and the
  // `total_amount` sent to the backend always reflect the method the user
  // has actually selected on the payment step.
  const liveStage1 = useMemo(
    () => calculateStage1Quote(
      items.map(i => ({ price: Number(i.price) || 0, quantity: Number(i.quantity) || 1 })),
      paymentMethod,
    ),
    [items, paymentMethod],
  );

  // Keep `quote` and the breakdown in sync with items / paymentMethod. This
  // matters because the user can change paymentMethod on step 2 BEFORE paying,
  // and the three POST sites below all close over `quote` for total_amount.
  useEffect(() => {
    setQuote(liveStage1.paymentTotal);
    setStage1Breakdown(liveStage1);
  }, [liveStage1]);

  const calculateQuote = () => {
    setQuote(liveStage1.paymentTotal);
    setStage1Breakdown(liveStage1);
    setStep(2);
  };

  // BDD 1-A line 8-9 & 18: the item-entry step AND the payment page must both
  // show the estimated Bonus Reward alongside the payable amount —
  // QuickQuote.tsx already did this (todo.txt section 9), OrderPortal.tsx
  // never did (todo.txt 18.1). Same formula, computed off the raw item
  // total (not the discounted "Export-Advantaged Quote" figure).
  const rawItemTotal = items.reduce((acc, item) => acc + (Number(item.price) || 0) * (Number(item.quantity) || 1), 0);
  const estimatedReward = calculateEstimatedReward(rawItemTotal, items.length);

  // ── Checkout Form (rendered inside Elements wrapper) ──────────────

  const CheckoutFormComponent: React.FC<{ onComplete: (oid: string) => void }> = ({ onComplete }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [cardError, setCardError] = useState<string | null>(null);
    const [cardComplete, setCardComplete] = useState(false);

    const handlePay = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements || !quote) return;
      setProcessing(true);
      setCardError(null);

      try {
        const token = localStorage.getItem('access_token');
        const orderRes = await axios.post(
          API.createOrder(),
          {
            total_amount: quote,
            currency: 'gbp',
            items: items.map(({ retailer, url, price, category, quantity, size, color, discountCode }) => ({ retailer, url, price, category, quantity, size: size || undefined, color: color || undefined, discount_code: discountCode || undefined })),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const { client_secret, order_id } = orderRes.data;

        const cardNumber = elements.getElement(CardNumberElement);
        if (!cardNumber) { setCardError('Card element not found. Please refresh and try again.'); setProcessing(false); return; }

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
          payment_method: { card: cardNumber },
        });

        if (confirmError) {
          setCardError(confirmError.message || 'Payment failed. Please check your card details.');
          setProcessing(false);
          return;
        }

        if (paymentIntent?.status === 'requires_capture' || paymentIntent?.status === 'succeeded') {
          await axios.post(API.confirmPreauth(order_id), {}, { headers: { Authorization: `Bearer ${token}` } });
          onComplete(order_id);
        } else {
          setCardError('Unexpected payment status: ' + paymentIntent?.status + '. Please contact support.');
          setProcessing(false);
        }
      } catch (err: any) {
        setCardError(err.response?.data?.detail || err.message || 'Payment error. Please try again.');
        setProcessing(false);
      }
    };

    return (
      <form onSubmit={handlePay}>
        <div style={{ background: 'var(--text-charcoal)', color: '#fff', padding: 'clamp(20px, 5vw, 32px)', borderRadius: '8px', marginBottom: '24px' }}>
          <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '16px' }}>Amount to Pre-Authorize</p>
          <p style={{ fontSize: 'clamp(28px, 8vw, 36px)', fontWeight: 700, color: '#D4AF37', marginBottom: '20px' }}>
            £{quote?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
          <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '16px', display: 'block' }}>
            Card Number
          </label>
          <div style={{ padding: '12px', background: '#fff', borderRadius: '6px', marginBottom: '12px', minHeight: '44px', display: 'flex', alignItems: 'center', width: '100%', boxSizing: 'border-box' }}>
            <CardNumberElement
              options={{ style: stripeElementStyle }}
              onChange={(e) => {
                setCardComplete(e.complete);
                if (e.error) {
                  setCardError(e.error.message);
                } else {
                  setCardError(null);
                }
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                Expiry Date
              </label>
              <div style={{ padding: '12px', background: '#fff', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                <CardExpiryElement options={{ style: stripeElementStyle }} />
              </div>
            </div>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                CVC
              </label>
              <div style={{ padding: '12px', background: '#fff', borderRadius: '6px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                <CardCvcElement options={{ style: stripeElementStyle }} />
              </div>
            </div>
          </div>
        </div>

        {cardError && (
          <div style={{ color: '#FCA5A5', fontSize: '14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', background: 'rgba(239,68,68,0.1)', padding: '12px', borderRadius: '6px' }}>
            <AlertCircle size={14} /> {cardError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline" style={{ minWidth: '140px', flex: '1 1 auto' }} onClick={() => setStep(1)} disabled={processing}>
            Back
          </button>
          <button type="submit" className="btn btn-primary" style={{ minWidth: '200px', flex: '2 1 auto', height: '50px', fontSize: '16px' }} disabled={!stripe || !cardComplete || processing}>
            {processing ? <><Loader2 className="animate-spin" size={18} /> Processing...</> : <><ShieldCheck size={18} /> Confirm & Pay</>}
          </button>
        </div>

        <div style={{ marginTop: '24px', fontSize: '12px', color: '#64748B' }}>
          🔒 Secured by Stripe
        </div>
      </form>
    );
  };

  // ── Reward-balance payment (BDD 1-A: 「使用既有紅利金額全額抵扣」) ─────────
  const RewardPaymentButton: React.FC<{ onComplete: (oid: string) => void }> = ({ onComplete }) => {
    const [processing, setProcessing] = useState(false);
    const [payError, setPayError] = useState<string | null>(null);
    const insufficientBalance = rewardBalance !== null && quote !== null && rewardBalance < quote;

    const handlePay = async () => {
      if (!quote) return;
      setProcessing(true);
      setPayError(null);
      const token = localStorage.getItem('access_token');

      try {
        const orderRes = await axios.post(
          API.createOrder(),
          {
            total_amount: quote,
            currency: 'gbp',
            payment_method: 'reward',
            items: items.map(({ retailer, url, price, category, quantity, size, color, discountCode }) => ({ retailer, url, price, category, quantity, size: size || undefined, color: color || undefined, discount_code: discountCode || undefined })),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const { order_id } = orderRes.data;
        await axios.post(API.confirmPreauth(order_id), {}, { headers: { Authorization: `Bearer ${token}` } });
        onComplete(order_id);
      } catch (err: any) {
        setPayError(err.response?.data?.detail || err.message || 'Could not pay with reward balance. Please try again.');
        setProcessing(false);
      }
    };

    return (
      <div>
        <div style={{ background: 'var(--text-charcoal)', color: '#fff', padding: 'clamp(20px, 5vw, 32px)', borderRadius: '8px', marginBottom: '24px' }}>
          <p style={{ fontSize: '13px', opacity: 0.7, marginBottom: '8px' }}>Your Reward Balance</p>
          <p style={{ fontSize: 'clamp(24px, 6vw, 30px)', fontWeight: 700, color: '#D4AF37' }}>
            £{(rewardBalance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
        {insufficientBalance && (
          <div style={{ color: '#FCA5A5', fontSize: '14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', background: 'rgba(239,68,68,0.1)', padding: '12px', borderRadius: '6px' }}>
            <AlertCircle size={14} /> Your reward balance doesn't cover this order.
          </div>
        )}
        {payError && (
          <div style={{ color: '#FCA5A5', fontSize: '14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', background: 'rgba(239,68,68,0.1)', padding: '12px', borderRadius: '6px' }}>
            <AlertCircle size={14} /> {payError}
          </div>
        )}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline" style={{ minWidth: '140px', flex: '1 1 auto' }} onClick={() => setStep(1)} disabled={processing}>
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ minWidth: '200px', flex: '2 1 auto', height: '50px', fontSize: '16px' }}
            disabled={processing || insufficientBalance}
            onClick={handlePay}
          >
            {processing ? <><Loader2 className="animate-spin" size={18} /> Processing...</> : <><ShieldCheck size={18} /> Pay with Reward Balance</>}
          </button>
        </div>
      </div>
    );
  };

  // ── Bank-transfer order (BDD 1-A: 「或匯入 Stripe 專屬虛擬銀行帳號」 — no real
  // per-customer virtual account exists, ops confirms the transfer manually
  // once it lands; see todo.txt 13.3) ─────────────────────────────────────
  const BankTransferOrderButton: React.FC<{ onComplete: (oid: string) => void }> = ({ onComplete }) => {
    const [processing, setProcessing] = useState(false);
    const [payError, setPayError] = useState<string | null>(null);

    const handleCreate = async () => {
      if (!quote) return;
      setProcessing(true);
      setPayError(null);
      const token = localStorage.getItem('access_token');

      try {
        const orderRes = await axios.post(
          API.createOrder(),
          {
            total_amount: quote,
            currency: 'gbp',
            payment_method: 'bank_transfer',
            items: items.map(({ retailer, url, price, category, quantity, size, color, discountCode }) => ({ retailer, url, price, category, quantity, size: size || undefined, color: color || undefined, discount_code: discountCode || undefined })),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onComplete(orderRes.data.order_id);
      } catch (err: any) {
        setPayError(err.response?.data?.detail || err.message || 'Could not create order. Please try again.');
        setProcessing(false);
      }
    };

    return (
      <div>
        <div style={{ background: 'rgba(74, 93, 35, 0.05)', border: '1px solid rgba(74, 93, 35, 0.15)', padding: 'clamp(16px, 4vw, 24px)', borderRadius: '8px', marginBottom: '24px', textAlign: 'left' }}>
          <p style={{ fontSize: '13px', margin: 0 }}>
            After you create this order, our team will send you our bank account details. Your order is held until we manually confirm your transfer has arrived — this can take 1-2 business days.
          </p>
        </div>
        {payError && (
          <div style={{ color: '#991B1B', fontSize: '14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', background: '#FEF2F2', padding: '12px', borderRadius: '6px' }}>
            <AlertCircle size={14} /> {payError}
          </div>
        )}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline" style={{ minWidth: '140px', flex: '1 1 auto' }} onClick={() => setStep(1)} disabled={processing}>
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ minWidth: '200px', flex: '2 1 auto', height: '50px', fontSize: '16px' }}
            disabled={processing}
            onClick={handleCreate}
          >
            {processing ? <><Loader2 className="animate-spin" size={18} /> Creating...</> : 'Create Order'}
          </button>
        </div>
      </div>
    );
  };

  const handlePreauthComplete = async (orderId: string) => {
    setOrderId(orderId);
    setCompletedPaymentMethod(paymentMethod);
    setStep(4);

    // "Upload your List" (VIP multi-item routing): attach the customer's
    // uploaded photo to the order's first item so the buying team has it
    // for reference — a failed attach shouldn't block the completed order.
    if (listImage) {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const orderRes = await axios.get(API.getOrder(orderId), { headers });
        const itemId = orderRes.data.items?.[0]?.id;
        if (itemId) {
          const formData = new FormData();
          formData.append('file', listImage);
          await axios.post(API.uploadOrderItemImage(orderId, itemId), formData, {
            headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          });
        }
      } catch {
        // A failed image attach shouldn't block the completed order.
      }
    }
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <header style={{ marginBottom: 'clamp(24px, 5vw, 40px)', textAlign: 'center' }}>
          <h1 className="page-title" style={{ fontSize: 'clamp(22px, 5vw, 28px)' }}>Incord Smart Order Portal</h1>
          <p style={{ color: 'var(--accent-moss)', fontStyle: 'italic', fontSize: 'clamp(13px, 3vw, 15px)' }}>
            "Intentional Connection. To the Core."
          </p>
        </header>

        {/* VIP Routing Option */}
        <div className="section-card" style={{ 
          background: 'rgba(185, 151, 91, 0.05)', 
          border: '1px dashed var(--accent-gold)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'flex-start',
          padding: 'clamp(12px, 3vw, 25px)'
        }}>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>
            Submitting a multi-item seasonal order (5+ items)?
          </p>
          <input
            ref={listFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={handleListImageChange}
          />
          <button
            type="button"
            className="btn btn-outline"
            style={{ fontSize: '12px', padding: '8px 16px', width: '100%' }}
            onClick={() => listFileInputRef.current?.click()}
          >
            <Upload size={14} style={{ marginRight: '8px' }} /> Upload your List
          </button>
          {listImage && (
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--accent-moss)' }}>
              Attached: {listImage.name} — we'll include this with your order.{' '}
              <button
                type="button"
                onClick={() => setListImage(null)}
                style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: '12px' }}
              >
                Remove
              </button>
            </p>
          )}
          {listImageError && (
            <p style={{ margin: 0, fontSize: '12px', color: '#991B1B' }}>{listImageError}</p>
          )}
        </div>

        {step === 1 && (
          <div className="section-card" style={{ padding: 'clamp(16px, 4vw, 24px)' }}>
            <h2 className="card-title" style={{ fontSize: 'clamp(16px, 4vw, 18px)' }}>1. Request a Quote</h2>
            
            {items.map((item, index) => (
              <div key={item.id} style={{ 
                padding: 'clamp(12px, 3vw, 20px)', 
                border: '1px solid var(--border-light)', 
                borderRadius: '4px', 
                marginBottom: '20px',
                position: 'relative',
                background: index % 2 === 0 ? 'var(--white)' : 'var(--bg-oatmeal)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                  <h3 style={{ fontSize: '16px', fontFamily: 'var(--sans-font)' }}>Item #{index + 1}</h3>
                  {items.length > 1 && (
                    <button 
                      onClick={() => removeItem(item.id)}
                      style={{ background: 'none', border: 'none', color: '#cc0000', cursor: 'pointer' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Select Retailer</label>
                  <select 
                    className="form-input"
                    value={item.retailer}
                    onChange={(e) => updateItem(item.id, 'retailer', e.target.value)}
                  >
                    <option value="">Select a retailer...</option>
                    {RETAILERS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div className="form-group">
                    <label className="form-label">Paste URL</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="https://..."
                      value={item.url}
                      onChange={(e) => updateItem(item.id, 'url', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit Price (GBP £)</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0.00"
                      value={item.price || ''}
                      onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`item-quantity-${item.id}`}>Quantity</label>
                    <input
                      id={`item-quantity-${item.id}`}
                      type="number"
                      min={1}
                      className="form-input"
                      value={item.quantity || 1}
                      onChange={(e) => updateItem(item.id, 'quantity', Math.max(1, parseInt(e.target.value, 10) || 1))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`item-size-${item.id}`}>Size (optional)</label>
                    <input
                      id={`item-size-${item.id}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. M, UK 8, 42"
                      value={item.size}
                      onChange={(e) => updateItem(item.id, 'size', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor={`item-color-${item.id}`}>Color (optional)</label>
                    <input
                      id={`item-color-${item.id}`}
                      type="text"
                      className="form-input"
                      placeholder="e.g. Black"
                      value={item.color}
                      onChange={(e) => updateItem(item.id, 'color', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor={`item-discount-code-${item.id}`}>Discount Code (optional)</label>
                  <input
                    id={`item-discount-code-${item.id}`}
                    type="text"
                    className="form-input"
                    placeholder="e.g. SUMMER10"
                    value={item.discountCode}
                    onChange={(e) => updateItem(item.id, 'discountCode', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Curation Category</label>
                  <select
                    className="form-input"
                    value={item.category}
                    onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
            ))}

            {items.length < 5 && (
              <button 
                className="btn btn-outline" 
                onClick={addItem}
                style={{ width: '100%', marginBottom: '30px', borderStyle: 'dashed' }}
              >
                <Plus size={16} /> Add Another Item
              </button>
            )}

            {rawItemTotal > 0 && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'rgba(212, 175, 55, 0.08)', border: '1px solid rgba(212, 175, 55, 0.25)',
                borderRadius: '4px', padding: '14px 20px', marginTop: '20px',
              }}>
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Estimated Bonus Reward</span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--accent-gold)' }}>
                  £{estimatedReward.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            <div style={{ marginTop: '30px', borderTop: '1px solid var(--border-light)', paddingTop: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                <input 
                  type="checkbox" 
                  id="safety" 
                  checked={compliance.flightSafety}
                  onChange={(e) => setCompliance({ ...compliance, flightSafety: e.target.checked })}
                  style={{ marginTop: '3px', flexShrink: 0 }}
                />
                <label htmlFor="safety" style={{ fontSize: '14px', cursor: 'pointer' }}>
                  I acknowledge the flight safety regulations (No perfume/alcohol).
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px' }}>
                <input 
                  type="checkbox" 
                  id="quota" 
                  checked={compliance.quotaAcknowledge}
                  onChange={(e) => setCompliance({ ...compliance, quotaAcknowledge: e.target.checked })}
                  style={{ marginTop: '3px', flexShrink: 0 }}
                />
                <label htmlFor="quota" style={{ fontSize: '14px', cursor: 'pointer' }}>
                  Stock availability is subject to allocation.
                </label>
              </div>

              <button 
                className="btn btn-primary" 
                style={{ width: '100%', height: '50px', fontSize: '16px' }}
                disabled={!compliance.flightSafety || !compliance.quotaAcknowledge || items.some(i => !i.price || !i.retailer)}
                onClick={calculateQuote}
              >
                <Calculator size={18} /> Calculate Quote
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="section-card" style={{ textAlign: 'center', padding: 'clamp(24px, 6vw, 60px) clamp(16px, 4vw, 40px)' }}>
            <h2 className="card-title" style={{ fontSize: 'clamp(20px, 5vw, 24px)', marginBottom: '30px' }}>2. Review &amp; Secure Payment</h2>
            
            <div style={{ 
              background: 'var(--text-charcoal)', 
              color: 'var(--white)', 
              padding: 'clamp(20px, 5vw, 40px)', 
              borderRadius: '4px',
              marginBottom: '30px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }}>
              <p style={{ fontSize: 'clamp(12px, 3vw, 14px)', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.8, marginBottom: '10px' }}>
                Your Export-Advantaged Quote
              </p>
              <h3 style={{ fontSize: 'clamp(32px, 10vw, 48px)', color: 'var(--accent-gold)', marginBottom: '10px', wordBreak: 'break-word' }}>
                £{quote?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h3>
              <p style={{ fontSize: '12px', fontStyle: 'italic', opacity: 0.7 }}>
                (Quote includes secure local procurement.)
              </p>
              {stage1Breakdown && (
                <div style={{
                  marginTop: '20px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(255,255,255,0.15)',
                  textAlign: 'left',
                  fontSize: '13px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.75 }}>Item subtotal (net)</span>
                    <span>£{stage1Breakdown.netItemsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.75 }}>Reward rebate (8%)</span>
                    <span style={{ color: 'var(--accent-gold)' }}>−£{stage1Breakdown.rewardRebate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ opacity: 0.75 }}>Buyer / service fee</span>
                    <span style={{ color: 'var(--accent-moss)' }}>+£{stage1Breakdown.buyerFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  {paymentMethod === 'card' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ opacity: 0.75 }}>Card processing (3%)</span>
                      <span style={{ color: 'var(--accent-moss)' }}>+£{stage1Breakdown.processingFee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: '6px', paddingTop: '8px',
                    fontWeight: 600,
                  }}>
                    <span>Total to pay</span>
                    <span style={{ color: 'var(--accent-gold)' }}>£{stage1Breakdown.paymentTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
            </div>

            {/* BDD 1-A line 18: 「支付頁面...同時應顯示預估現金回饋，促使客戶下單」 */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'rgba(212, 175, 55, 0.08)', border: '1px solid rgba(212, 175, 55, 0.25)',
              borderRadius: '4px', padding: '14px 20px', marginBottom: '30px', textAlign: 'left',
            }}>
              <span style={{ fontSize: '13px', fontWeight: 500 }}>Estimated Bonus Reward</span>
              <span style={{ fontSize: '20px', fontWeight: 700, color: 'var(--accent-gold)' }}>
                £{estimatedReward.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'start', 
              gap: '15px', 
              textAlign: 'left', 
              background: 'rgba(74, 93, 35, 0.05)', 
              padding: 'clamp(12px, 3vw, 20px)',
              borderRadius: '4px',
              marginBottom: '40px',
              border: '1px solid rgba(74, 93, 35, 0.1)'
            }}>
              <AlertCircle size={20} color="var(--accent-moss)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ fontSize: '13px', margin: 0 }}>
                <strong>International Transit:</strong> Please note that international shipping fees are calculated separately upon shipment and are not included in this purchase quote. Incord reserves the right to adjust final shipping fees for extraordinarily bulky items prior to dispatch.
              </p>
            </div>

            {/* Payment method selector (BDD 1-A line 20) */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {([
                { value: 'card' as const, label: 'Credit / Debit Card' },
                { value: 'bank_transfer' as const, label: 'Bank Transfer' },
                { value: 'reward' as const, label: 'Reward Balance' },
              ]).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={paymentMethod === opt.value ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ flex: '1 1 160px', fontSize: '13px' }}
                  onClick={() => setPaymentMethod(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {paymentMethod === 'card' && (
              <Elements stripe={stripePromise}>
                <CheckoutFormComponent onComplete={handlePreauthComplete} />
              </Elements>
            )}
            {paymentMethod === 'reward' && (
              <RewardPaymentButton onComplete={handlePreauthComplete} />
            )}
            {paymentMethod === 'bank_transfer' && (
              <BankTransferOrderButton onComplete={handlePreauthComplete} />
            )}

            <div style={{ marginTop: '20px' }}>
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => setStep(1)}>
                Back to Edit Items
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="section-card" style={{ textAlign: 'center', padding: 'clamp(24px, 6vw, 60px) clamp(16px, 4vw, 40px)' }}>
            <div style={{ 
              width: 'clamp(60px, 15vw, 80px)', 
              height: 'clamp(60px, 15vw, 80px)', 
              background: 'var(--accent-moss)', 
              color: 'white', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              margin: '0 auto 30px'
            }}>
              <ShieldCheck size={40} />
            </div>
            <h2 className="card-title" style={{ fontSize: 'clamp(24px, 6vw, 28px)' }}>
              {completedPaymentMethod === 'bank_transfer' ? 'Order Created!' : 'Order Locked!'}
            </h2>
            {orderId && (
              <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '8px' }}>
                Order ID: <strong>{orderId.split('-')[0].toUpperCase()}</strong>
              </p>
            )}
            <p style={{ color: '#666', marginBottom: '40px' }}>
              {completedPaymentMethod === 'bank_transfer'
                ? 'We’ll email you our bank details. Your order is held until our team manually confirms your transfer has arrived.'
                : 'Your allocation request has been secured with a pre-authorization hold. Our local team is now working to confirm your items.'}
            </p>
            
            <div style={{ background: 'var(--bg-oatmeal)', padding: 'clamp(16px, 4vw, 20px)', borderRadius: '4px', marginBottom: '40px', textAlign: 'left' }}>
              <p style={{ fontSize: '13px', margin: '0 0 10px' }}><strong>What happens next?</strong></p>
              <ul style={{ fontSize: '13px', paddingLeft: '20px', color: '#555' }}>
                <li>Successful: Funds captured & order proceeds to fulfillment.</li>
                <li>Unavailable: Funds released immediately (no fees).</li>
                <li>You can track progress in your Dashboard & Orders page.</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexDirection: 'column' }}>
              <button className="btn btn-outline" style={{ width: '100%' }} onClick={() => navigate('/orders')}>
                <ExternalLink size={16} /> View My Orders
              </button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => navigate('/dashboard')}>
                Go to Dashboard <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default OrderPortal;
